// @flow
import { createSelector } from 'reselect';

import type { State } from 'store/State';
import type { IPersonInfo } from './IPersonInfo';
// included twice because the appointment personInfo may be null
import type { PersonInfo } from 'api/xws/login/entities/PersonInfo';
import type { Appointment } from 'api/xws/login/entities/Appointment';
import type { Vehicle } from 'api/xws/login/entities/Vehicle';
import type { ContactInfo } from 'api/xws/login/entities/ContactInfo';

// Selectors
import {
  getSupportedCountries,
  getDealerCountryCode
} from 'store/reducers/dealerSettings/selectors';
import { getStates } from 'store/reducers/states/selectors';
import { getPersonId as getPersonIdFromLogin } from 'store/reducers/login/selectors';
import {
  getVehicleProperty,
  getContactInfo as getContactInfoAppt
} from 'store/reducers/appointment/selectors';
import { isPopulateMobilePhoneNumber } from 'store/reducers/userInteractions/selectors';
import { getObfuscatedBy } from 'store/reducers/appointment/contactInfo/selectors';

// Utils
import { isPhoneNumberValid } from 'utils/string';

// self
export const getPersonInfo = (state: State): IPersonInfo =>
  state.user.personInfo;

export function getProperty(
  state: State,
  property: $Keys<PersonInfo>
): $Values<PersonInfo> | void {
  const personInfo = getPersonInfo(state);
  if (personInfo) {
    return personInfo[property] || '';
  }
}

/**
 * Returns the list of vehicles
 */
export const getVehicles = createSelector(
  [getPersonInfo],
  (personInfo: IPersonInfo): Vehicle[] | void => {
    if (personInfo && personInfo.vehicles) {
      return personInfo.vehicles;
    }
  }
);

/**
 * Returns the first appointment in the list of vehicles of the user
 */
export const getFirstAppointment = createSelector(
  [getPersonInfo],
  (personInfo: IPersonInfo): Appointment | void => {
    if (personInfo && personInfo.vehicles) {
      // regular for loop to find the first appointment to return it right away
      for (let i = 0; i < personInfo.vehicles.length; i += 1) {
        const vehicle = personInfo.vehicles[i];
        if (vehicle.appointmentList.length) {
          return vehicle.appointmentList[0];
        }
      }
    }
  }
);

export const getContactInfo = createSelector(
  [getPersonInfo],
  (personInfo: IPersonInfo): ContactInfo | void => {
    if (personInfo && personInfo.contactInfo) {
      return personInfo.contactInfo;
    }
  }
);

/**
 * Returns the first phone number in the contact info that has digits
 * NOTE: it's not a regular selector that receives state
 */
export function getFirstPhoneNumber(contactInfo: ContactInfo): string {
  if (contactInfo) {
    for (let i = 0; i < contactInfo.phoneNumbers.length; i += 1) {
      const phoneNumber = contactInfo.phoneNumbers[i];
      if (phoneNumber.digits) {
        return phoneNumber.digits;
      }
    }
  }
  return '';
}

export function getTypeAndPhoneNumber(state: State) {
  const contactInfo = getContactInfo(state);
  if (contactInfo) {
    for (let i = 0; i < contactInfo.phoneNumbers.length; i += 1) {
      const phoneNumber = contactInfo.phoneNumbers[i];
      if (phoneNumber.digits) {
        return phoneNumber;
      }
    }
    return null;
  }
}

export const getPhoneNumber = createSelector(
  getPersonInfo,
  personInfo => (type: string) => {
    if (personInfo && personInfo.contactInfo) {
      const contactInfo = personInfo.contactInfo;
      let i;
      for (i = 0; i < contactInfo.phoneNumbers.length; i += 1) {
        const phoneNumber = contactInfo.phoneNumbers[i];
        if (phoneNumber.type === type) {
          return phoneNumber.digits;
        }
      }
    }
    return '';
  }
);

export const getPersonId = createSelector(
  getPersonInfo,
  getPersonIdFromLogin,
  getVehicleProperty,
  getContactInfoAppt,
  (personInfo, personIdFromLogin, vehicleProperty, contactInfo) => {
    return (
      personIdFromLogin ||
      vehicleProperty('personId') ||
      (personInfo && personInfo.personId) ||
      contactInfo.id
    );
  }
);

export function getContactInfoProperty(
  state: State,
  property: $Keys<ContactInfo>
): $Values<ContactInfo> | void {
  const contactInfo = getContactInfo(state);
  if (contactInfo) {
    return contactInfo[property];
  }
}

export const getProfileInitialValues = createSelector(
  getPersonInfo,
  getSupportedCountries,
  getStates,
  getPhoneNumber,
  (
    { contactInfo, firstName, lastName },
    supportedCountries,
    states,
    getPhoneNumber
  ) => ({
    firstName,
    lastName,
    mobilePhoneNumber: getPhoneNumber('MOBILE') || '',
    phoneNumber: getPhoneNumber('HOME') || '',
    email: contactInfo.emailAddress.email,
    country: supportedCountries.find(
      country => country.value === contactInfo.address.country
    ),
    state: {
      value: contactInfo.address.state,
      label: contactInfo.address.state
    },
    streetAddressOne: contactInfo.address.streetAddressOne,
    streetAddressTwo: contactInfo.address.streetAddressTwo,
    city: contactInfo.address.city,
    postalCode: contactInfo.address.postalCode
  })
);

const getMobileNumber = createSelector(
  getPhoneNumber,
  getContactInfoAppt,
  (getPhoneNumber, contactInfo) => {
    if (getPhoneNumber('MOBILE')) {
      return getPhoneNumber('MOBILE');
    } else if (contactInfo && contactInfo.mobilePhone) {
      return contactInfo.mobilePhone;
    } else {
      return '';
    }
  }
);

export const showPhoneNumber = createSelector(
  getPhoneNumber,
  getContactInfoAppt,
  getMobileNumber,
  isPopulateMobilePhoneNumber,
  getDealerCountryCode,
  getObfuscatedBy,
  (
    getPhoneNumber,
    contactInfo,
    mobileNumber,
    isPopulateMobilePhoneNumber,
    dealerCountryCode,
    obfuscatedBy
  ) => {
    if (isPopulateMobilePhoneNumber) {
      if (mobileNumber) return mobileNumber;
      else if (contactInfo && contactInfo.phone) {
        if (
          obfuscatedBy === 'email' ||
          obfuscatedBy === 'licenseNumber' ||
          obfuscatedBy === 'deeplink'
        ) {
          return contactInfo.phone;
        }
        if (isPhoneNumberValid(contactInfo.phone, dealerCountryCode)) {
          return contactInfo.phone;
        }
      }
    } else {
      return '';
    }
  }
);

export const getPrivacyPreferences = createSelector(
  getContactInfo,
  contactInfo => {
    if (!contactInfo) {
      return null;
    } else {
      const {
        emailPrivacyPreference,
        textPrivacyPreference,
        phonePrivacyPreference
      } = contactInfo;
      return {
        emailPrivacyPreference,
        textPrivacyPreference,
        phonePrivacyPreference
      };
    }
  }
);



// WEBPACK FOOTER //
// ./src/store/reducers/user/personInfo/selectors.js